import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, useCurrent } from "./common";
import { PageHead, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { ScopeConfigurator } from "../components/ScopeConfigurator";
import { actions, STUDIO } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { defaultConfig } from "../lib/rates";
import { fmtDate } from "../lib/format";

export default function Estimator() {
  const p = useCurrent();
  const nav = useNavigate();
  const session = useSession();
  const canEdit = useCan("estimator");
  const [lockOpen, setLockOpen] = useState(false);
  if (!p) return null;
  const config = p.estimate ?? defaultConfig(p.budget, p.currency);
  const lock = p.feeLock;
  const editable = canEdit && !lock;

  return (
    <Page>
      <PageHead kicker={`${p.name} · Fee builder`} title="Scope & cost estimator">
        {editable && <button className="btn accent sm" onClick={() => { actions.setEstimate(p.id, config); actions.saveQuoteFromEstimate(p.id, "Studio"); nav(`/studio/${p.id}/quotes`); }}>
          <Icon.money width={15} />Save as quotation</button>}
        {canEdit && (lock
          ? <button className="btn ghost sm" onClick={() => actions.setFeeLock(p.id, false, session?.name ?? STUDIO.principal)}>
              <Icon.sliders width={15} />Unlock to change</button>
          : <button className="btn ghost sm" onClick={() => setLockOpen(true)}>
              <Icon.check width={15} />Lock configuration</button>)}
        <button className="btn ghost sm" onClick={() => nav(`/portal/${p.id}/configure`)}><Icon.arrowUpRight width={15} />Client view</button>
      </PageHead>

      {lock ? (
        <div className="card pad reveal" style={{ marginBottom: 22, background: "color-mix(in srgb, var(--ok) 8%, transparent)" }}>
          <div className="row between wrap" style={{ gap: 12 }}>
            <div>
              <div className="kicker">Fee configuration locked</div>
              <p className="muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.6, maxWidth: 620 }}>
                Agreed and frozen on {fmtDate(lock.on)} by {lock.by}. Your client still sees the whole
                scope and fee in their portal, but can no longer change it or send a revised scope.
                {lock.note ? ` — ${lock.note}` : ""}
              </p>
            </div>
            <Icon.check width={22} style={{ color: "var(--ok)", flexShrink: 0 }} />
          </div>
        </div>
      ) : (
        <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 24, fontSize: 16 }}>
          Build the design scope discipline by discipline. Fees are computed from your rate card against
          the construction budget — adjust the scope and the fee responds live. Lock it once it is agreed.
        </p>
      )}

      <div className="reveal">
        <ScopeConfigurator config={config} onChange={editable ? (c) => actions.setEstimate(p.id, c) : undefined} canDiscount={editable} readOnly={!editable}
          lockedNote={lock ? "Locked — this is the agreed scope. Unlock above to change it." : undefined} />
      </div>

      {lockOpen && <LockModal pid={p.id} by={session?.name ?? STUDIO.principal} onClose={() => setLockOpen(false)} />}
    </Page>
  );
}

/* The note is what the client reads when they find the configurator frozen —
   worth a sentence rather than a silent lockout. */
function LockModal({ pid, by, onClose }: { pid: string; by: string; onClose: () => void }) {
  const [note, setNote] = useState("Scope and fee agreed.");
  return (
    <Modal open onClose={onClose} title="Lock the fee configuration">
      <div className="col" style={{ gap: 12 }}>
        <p className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
          The client keeps full sight of the agreed scope and fee — every discipline, service and figure
          stays visible in their portal. What they lose is the ability to change it or send a revised
          scope. You can unlock it again at any time.
        </p>
        <div className="field">
          <label>Note to the client (optional)</label>
          <input className="input" autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="Scope and fee agreed." />
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" onClick={() => { actions.setFeeLock(pid, true, by, note); onClose(); }}>
            <Icon.check width={15} />Lock it
          </button>
        </div>
      </div>
    </Modal>
  );
}
